import { useState, useEffect, useCallback } from 'react';
import { consentManager, ConsentState, ConsentCategory } from '../utils/consentManager';

/**
 * React hook for managing consent state in components
 * 
 * @returns Consent state and helper functions
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isConsented, grantConsent, denyConsent, hasConsentChoice } = useConsent();
 *   
 *   if (!hasConsentChoice()) {
 *     return <p>Please make a consent choice</p>;
 *   }
 *   
 *   return (
 *     <div>
 *       {isConsented('analytics') ? (
 *         <AnalyticsDashboard />
 *       ) : (
 *         <p>Analytics disabled</p>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useConsent() {
  const [consentState, setConsentState] = useState<ConsentState>(consentManager.getState());

  useEffect(() => {
    // Subscribe to consent changes
    const unsubscribe = consentManager.subscribe((newState) => {
      setConsentState(newState);
    });

    return unsubscribe;
  }, []);

  /**
   * Check if a specific category is consented
   */
  const isConsented = useCallback((category: ConsentCategory): boolean => {
    return consentManager.isConsented(category);
  }, []);

  /**
   * Grant consent for specific categories
   */
  const grantConsent = useCallback((categories: ConsentCategory[]) => {
    consentManager.grantConsent(categories);
  }, []);

  /**
   * Deny consent for specific categories
   */
  const denyConsent = useCallback((categories: ConsentCategory[]) => {
    consentManager.denyConsent(categories);
  }, []);

  /**
   * Grant all consent categories
   */
  const grantAllConsent = useCallback(() => {
    consentManager.grantAllConsent();
  }, []);

  /**
   * Deny all non-essential consent categories
   */
  const denyAllConsent = useCallback(() => {
    consentManager.denyAllConsent();
  }, []);

  /**
   * Check if user has made any consent choice
   */
  const hasConsentChoice = useCallback((): boolean => {
    return consentManager.hasConsentChoice();
  }, []);

  /**
   * Get consent summary for display
   */
  const getConsentSummary = useCallback(() => {
    return consentManager.getConsentSummary();
  }, []);

  /**
   * Reset consent (for testing or policy updates)
   */
  const resetConsent = useCallback(() => {
    consentManager.resetConsent();
  }, []);

  return {
    consentState,
    isConsented,
    grantConsent,
    denyConsent,
    grantAllConsent,
    denyAllConsent,
    hasConsentChoice,
    getConsentSummary,
    resetConsent,
  };
}

export default useConsent;
